import cx from 'classnames';
import type { ReactNode } from 'react';
import React from 'react';
import { Link } from 'react-router-dom';
import type { JSX } from '@gov-design-system-ce/components';
import { GovIcon } from '@gov-design-system-ce/react';
import type { Nullable, Optional } from '@gov-nx/core/types';
import { Button } from '../Button/Button';
import { Icon } from '../Icon';

interface DateAndIconProps {
	icon?: Nullable<JSX.GovIcon>;
	date?: string;
	iconWrapper?: (children: ReactNode) => ReactNode;
}

const DateAndIcon = ({ date, icon, iconWrapper }: DateAndIconProps) => (
	<>
		{date && (
			<time className="relative top-auto right-auto inline-block mt-2 text-(--text-secondary)! [ md:absolute md:top-[26px] md:right-[58px] md:mt-0 ]">
				{date}
			</time>
		)}
		{icon ? (
			<span className="absolute top-7 right-6 text-(--icon-on-subtle)">
				{iconWrapper ? (
					iconWrapper(
						<GovIcon
							type={icon.type}
							name={icon.name}
							size="l"
						/>
					)
				) : (
					<GovIcon
						type={icon.type}
						name={icon.name}
						size="l"
					/>
				)}
			</span>
		) : null}
	</>
);

const LoadingIcon = () => (
	<Button
		isLoading={true}
		color="primary"
		type="base"
		size="s"
		iconStart={
			<Icon
				type="templates"
				name="download"
			/>
		}
	/>
);

export interface TileProps {
	children?: React.ReactNode;
	customClasses?: string;
	icon?: Nullable<JSX.GovIcon>;
	name: string;
	target?: JSX.GovButton['target'];
	to?: Optional<string>;
	date?: string;
	headlineLink?: boolean;
	iconLink?: boolean;
	loading?: boolean;
	onClick?: (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void;
}

export const Tile = ({
	children,
	customClasses,
	icon,
	name,
	target,
	to,
	date,
	headlineLink,
	iconLink,
	loading,
	onClick,
}: TileProps) => {
	const linkTarget = { target };

	const tileLink = (children: ReactNode, classNames?: Optional<string>) => {
		if (to) {
			return (
				<Link
					className={classNames}
					to={to}
					{...linkTarget}>
					{children}
				</Link>
			);
		} else if (onClick) {
			return (
				<Link
					className={classNames}
					to="#"
					onClick={(event) => {
						onClick(event);
					}}>
					{children}
				</Link>
			);
		}
		return children;
	};

	return (
		<li
			className={cx(
				'border-b border-(--border-subtlest) [ last-of-type:border-b-0 ]',
				customClasses
			)}>
			{to || onClick ? (
				<div>
					{headlineLink || iconLink ? (
						<div className="flex flex-col gap-(--spacing-xs) relative p-(--templates-margin-l) [ md:gap-(--spacing-s) ]">
							{tileLink(
								<h3
									className={cx(
										'inline-block pr-6 text-body-l-bold! underline [ hover:no-underline ]',
										date ? '[ md:max-w-[70%] ] [ xl:max-w-[70%] xl:pr-0 ]' : ''
									)}>
									{loading && <LoadingIcon />}
									{name}
								</h3>,
								'relative z-[1] size-fit'
							)}
							<div>{children}</div>
							<DateAndIcon
								icon={icon}
								date={date}
								iconWrapper={
									iconLink ? (children) => tileLink(children) : undefined
								}
							/>
						</div>
					) : (
						tileLink(
							<>
								<h3 className="pr-6 text-body-l-bold! text-(--text-primary-color) [ md:max-w-[70%] ] [ xl:max-w-[70%] xl:pr-0 ]">
									{loading && <LoadingIcon />}
									{name}
								</h3>
								<div>{children}</div>
								<DateAndIcon
									icon={icon}
									date={date}
								/>
							</>,
							'anim-bg-hover relative flex flex-col gap-(--spacing-xs) p-(--templates-margin-l) no-underline! [ md:gap-(--spacing-s) ]'
						)
					)}
				</div>
			) : (
				<div className="flex flex-col gap-(--spacing-xs) relative p-(--templates-margin-l) text-(--text-secondary) [ md:gap-(--spacing-s) ]">
					<h3
						className={cx(
							'inline-block pr-6 text-body-l-bold! text-(--text-primary-color)',
							date ? '[ md:max-w-[70%] ] [ xl:max-w-[70%] xl:pr-0 ]' : ''
						)}>
						{loading && <LoadingIcon />}
						{name}
					</h3>
					<div>{children}</div>
					<DateAndIcon
						icon={icon}
						date={date}
					/>
				</div>
			)}
		</li>
	);
};
